import React from 'react';
import { FiDownload } from 'react-icons/fi';

// ── Spreadsheet-style table (rows × columns, grid lines, sticky header,
// row numbers, optional totals row) + "Download Excel" (CSV opens in Excel).
// columns: [{ key, label, align?: 'right', format?: (value,row)=>node, csv?: (value,row)=>string, total?: true }]
const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function ExcelTable({ title, columns, rows, fileName = 'report', maxHeight = 480, totals = true, onRowClick }) {
  const totalRow = totals && columns.some(c => c.total)
    ? Object.fromEntries(columns.filter(c => c.total).map(c => [c.key, rows.reduce((a, r) => a + (Number(r[c.key]) || 0), 0)]))
    : null;

  const download = () => {
    const header = columns.map(c => csvCell(c.label)).join(',');
    const body = rows.map(r => columns.map(c => csvCell(c.csv ? c.csv(r[c.key], r) : r[c.key])).join(','));
    const lines = [header, ...body];
    if (totalRow) lines.push(columns.map((c, i) => csvCell(c.total ? Math.round(totalRow[c.key] * 100) / 100 : (i === 0 ? 'TOTAL' : ''))).join(','));
    // BOM so Excel reads Arabic names correctly
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${fileName}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const cell = { border: '1px solid #d9dee5', padding: '7px 10px', fontSize: 13, whiteSpace: 'nowrap' };
  const head = { ...cell, background: '#e8f0e9', color: '#14532d', fontWeight: 700, position: 'sticky', top: 0, zIndex: 1 };
  const rowNo = { ...cell, background: '#f3f4f6', color: '#6b7280', textAlign: 'center', width: 36, fontSize: 12 };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong>{title}</strong>
        <button className="btn btn-outline btn-sm" onClick={download} disabled={!rows.length}>
          <FiDownload size={14} style={{ marginRight: 6 }} />Download Excel
        </button>
      </div>
      <div style={{ overflow: 'auto', maxHeight }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 'max-content' }}>
          <thead>
            <tr>
              <th style={{ ...head, ...rowNo, background: '#e5e7eb', color: '#6b7280' }}>#</th>
              {columns.map(c => <th key={c.key} style={{ ...head, textAlign: c.align || 'left' }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} style={{ ...cell, textAlign: 'center', color: '#94a3b8' }}>No data</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r._key || i} style={{ background: i % 2 ? '#fafafa' : '#fff', cursor: onRowClick ? 'pointer' : 'default' }}
                onClick={onRowClick ? () => onRowClick(r) : undefined}>
                <td style={rowNo}>{i + 1}</td>
                {columns.map(c => (
                  <td key={c.key} style={{ ...cell, textAlign: c.align || 'left' }}>
                    {c.format ? c.format(r[c.key], r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {totalRow && rows.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...rowNo, fontWeight: 700, position: 'sticky', bottom: 0 }}>Σ</td>
                {columns.map((c, i) => (
                  <td key={c.key} style={{ ...cell, background: '#fef9c3', fontWeight: 800, textAlign: c.align || 'left', position: 'sticky', bottom: 0 }}>
                    {c.total ? (c.format ? c.format(totalRow[c.key], totalRow) : totalRow[c.key]) : (i === 0 ? 'TOTAL' : '')}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
